/**
 * Contractul dintre core, web si POS.
 *
 * Modulul e deliberat fara dependinte: e importat si de bundle-ul de browser,
 * si de core, si de codul din nexuspos care implementeaza poarta POS. Orice
 * dependinta adaugata aici ajunge in trei artefacte deodata.
 *
 * Trei suprafete, cu reguli diferite:
 *  - web -> core: public, fara secrete, versionat prin `X-Core-Contract`;
 *  - core -> POS: `/api/delivery-platform/*`, o singura poarta (ADR-013);
 *  - POS -> core: webhook-uri semnate, idempotente.
 */
// ---------------------------------------------------------------------------
// Versionarea contractului (ADR-002)
// ---------------------------------------------------------------------------
/** Contractul pe care il vorbeste core-ul din acest build. */
export const CORE_CONTRACT_VERSION = 1;
/**
 * Cel mai vechi contract pe care core-ul il mai serveste. ADR-002 cere N si
 * N-1 simultan; la N=1 nu exista N-1, deci minimul e tot 1. Cand contractul
 * urca la 2, aceasta constanta devine 1, nu 2.
 */
export const CORE_CONTRACT_MIN_SUPPORTED = 1;
export const HEADER_CORE_CONTRACT = "x-core-contract";
export const HEADER_PLATFORM_INSTANCE = "x-platform-instance";
export const HEADER_REQUEST_ID = "x-request-id";
export const HEADER_IDEMPOTENCY_KEY = "idempotency-key";
/**
 * Maximul de bucati pe o linie de cos.
 *
 * Traieste in contract fiindca are DOUA capete care trebuie sa spuna acelasi
 * lucru: core-ul respinge la finalizare orice linie peste valoarea asta, iar
 * vitrina trebuie sa opreasca inainte, ca sa nu lase clientul sa construiasca un
 * cos care va fi refuzat. Scrisa de doua ori, cele doua ar diverge tacit, si
 * singurul care ar afla ar fi clientul, in ultimul pas.
 */
export const MAX_QTY_PER_LINE = 50;
export const HEADER_SIGNATURE = "x-deliveryapp-signature";
export const HEADER_CONFIG_VERSION = "x-config-version";
/** ADR-013: tot ce e expus platformei sta sub acest prefix, nicaieri altundeva. */
export const POS_GATEWAY_PREFIX = "/api/delivery-platform";
// ---------------------------------------------------------------------------
// Caile publice web -> core (defect A1)
// ---------------------------------------------------------------------------
/**
 * Caile publice, ca SURSA UNICA pentru ambele capete.
 *
 * Motivul e un defect masurat, nu o preferinta de stil: web-ul chema
 * `/api/storefronts/:slug`, `/api/quote`, `/api/checkout` si
 * `GET /api/orders/:code`, iar core-ul expunea `/api/storefront/:slug`,
 * `/api/quotes`, `POST /api/orders` si `POST /api/orders/:code/urmarire`.
 * Nicio comanda nu se putea plasa pe nicio piata, iar nimic din build nu semnala
 * divergenta, pentru ca fiecare capat isi scria propriul literal.
 *
 * Numele canonice sunt cele din core: core e implementarea, web-ul se aliniaza.
 * Singura intrare care nu exista inca in core e `geocode` — e declarata aici ca
 * sa fie implementata sub numele pe care web-ul il apeleaza deja, nu inca unul.
 *
 * `path` e sablonul in forma Express (`:param`), pentru inregistrarea rutei.
 * Clientii NU concateneaza sablonul: folosesc `publicPath.*`, care substituie si
 * encodeaza parametrii.
 */
export const PUBLIC_ROUTES = {
    health: { method: "GET", path: "/api/health" },
    bootstrap: { method: "GET", path: "/api/bootstrap/:market" },
    storefront: { method: "GET", path: "/api/storefront/:slug" },
    /** Verificarea acoperirii pentru o adresa (previzualizare, ADR-003). */
    storefrontAddress: { method: "POST", path: "/api/storefront/:slug/adresa" },
    /**
     * Acoperirea TUTUROR vitrinelor pietei pentru o adresa, dintr-o singura
     * cerere. Exista pentru pagina de intrare a unei piete cu mai multe
     * restaurante: fara ea, web-ul ar fi cerut `storefrontAddress` de N ori ca sa
     * poata spune care restaurante ajung la client — sau nu ar fi spus deloc, si
     * clientul ar fi ales un restaurant care nu livreaza la el, ca sa afle asta
     * abia in meniu. Raspunsul e o PREVIZUALIZARE pe zona, fara cos (ADR-003).
     *
     * Ruta e noua fata de contractul 1 initial; un core mai vechi raspunde 404
     * `ROUTE_NOT_FOUND`, iar web-ul trateaza asta ca „nu stiu", nu ca „nu livram".
     */
    storefrontCoverage: { method: "POST", path: "/api/storefronts/acoperire" },
    quote: { method: "POST", path: "/api/quotes" },
    checkout: { method: "POST", path: "/api/orders" },
    /**
     * Urmarirea comenzii. Metoda e POST, nu GET, si telefonul e OBLIGATORIU in
     * corp: codul comenzii e scurt si ghicibil, iar in spatele lui stau numele,
     * telefonul si adresa clientului.
     */
    orderTracking: { method: "POST", path: "/api/orders/:code/urmarire" },
    /**
     * Geocodare (defect A1/G3). Fara ea, `QuoteRequest.dropoff` cere `lat`/`lng`
     * pe care clientul fara geolocatie functionala nu le poate obtine deloc.
     */
    geocode: { method: "POST", path: "/api/geocode" },
    /**
     * Geocodare INVERSA: din coordonatele date de browser („Foloseste locatia
     * mea") in strada, numar si localitate. Fara ea, butonul de locatie dadea
     * doar un punct, iar clientul trebuia sa scrie oricum strada si orasul de
     * mana — adica exact munca pe care butonul promitea sa o scuteasca.
     * Optionala in contract, ca `storefrontCoverage`: un core mai vechi raspunde
     * 404, iar formularul ramane completabil de mana.
     */
    geocodeReverse: { method: "POST", path: "/api/geocode/invers" },
    /**
     * Rezervarile de masa. Sunt o functie a RESTAURANTULUI, nu a platformei:
     * core-ul nu tine mese, zone de sala sau politici de rezervare, ci intreaba
     * POS-ul vendorului prin aceeasi poarta ca la comenzi (ADR-013). Exista aici
     * fiindca o piata poate fi si site-ul unui grup de restaurante, nu doar un
     * canal de livrare — iar acolo rezervarea e a doua actiune ca importanta dupa
     * comanda.
     */
    reservationVendors: { method: "GET", path: "/api/reservations/restaurante" },
    reservationSlots: { method: "POST", path: "/api/reservations/:vendorSlug/intervale" },
    reservationCreate: { method: "POST", path: "/api/reservations/:vendorSlug" },
    /**
     * Inregistrarea deciziei de consimtamant (bannerul de urmarire).
     *
     * Publica si neautentificata prin natura ei: se cheama la PRIMA vizita, cand
     * nu exista nici cont, nici comanda, nici client in baza. Dovada se leaga de
     * un identificator anonim generat local.
     *
     * Scrierea in jurnal NU e conditia ca decizia sa se aplice: browserul o
     * respecta imediat, local. Daca cererea catre server pica, urmarirea tot nu
     * porneste — invers ar insemna ca o eroare de retea produce urmarire
     * neconsimtita.
     */
    consent: { method: "POST", path: "/api/consimtamant" },
};
function fillPath(template, params) {
    return template.replace(/:([A-Za-z_][A-Za-z0-9_]*)/g, (_match, key) => {
        const value = params[key];
        if (typeof value !== "string" || value.length === 0) {
            // Fara asta, un parametru gol produce `/api/storefront/` — adica un 404 pe
            // alta ruta, care se citeste ca "vitrina nu exista".
            throw new Error(`Parametrul «${key}» lipseste din calea «${template}».`);
        }
        return encodeURIComponent(value);
    });
}
/** Caile concrete, construite din aceleasi sabloane pe care le inregistreaza core-ul. */
export const publicPath = {
    health: () => PUBLIC_ROUTES.health.path,
    bootstrap: (market) => fillPath(PUBLIC_ROUTES.bootstrap.path, { market }),
    storefront: (slug) => fillPath(PUBLIC_ROUTES.storefront.path, { slug }),
    storefrontAddress: (slug) => fillPath(PUBLIC_ROUTES.storefrontAddress.path, { slug }),
    storefrontCoverage: () => PUBLIC_ROUTES.storefrontCoverage.path,
    quote: () => PUBLIC_ROUTES.quote.path,
    checkout: () => PUBLIC_ROUTES.checkout.path,
    orderTracking: (code) => fillPath(PUBLIC_ROUTES.orderTracking.path, { code }),
    geocode: () => PUBLIC_ROUTES.geocode.path,
    geocodeReverse: () => PUBLIC_ROUTES.geocodeReverse.path,
    consent: () => PUBLIC_ROUTES.consent.path,
    reservationVendors: () => PUBLIC_ROUTES.reservationVendors.path,
    reservationSlots: (vendorSlug) => fillPath(PUBLIC_ROUTES.reservationSlots.path, { vendorSlug }),
    reservationCreate: (vendorSlug) => fillPath(PUBLIC_ROUTES.reservationCreate.path, { vendorSlug }),
};
// ---------------------------------------------------------------------------
// Erori
// ---------------------------------------------------------------------------
/** Coduri intoarse de POS catre core, pe poarta `/api/delivery-platform/*`. */
export const POS_ERROR_CODES = {
    INVALID_CHANNEL_SECRET: "INVALID_CHANNEL_SECRET",
    CHANNEL_PAUSED: "CHANNEL_PAUSED",
    ORDER_NOT_FOUND: "ORDER_NOT_FOUND",
    ALREADY_COMMITTED: "ALREADY_COMMITTED",
    ORDER_NOT_READY: "ORDER_NOT_READY",
    COVERAGE_INSUFFICIENT: "COVERAGE_INSUFFICIENT",
    PAYMENT_METHOD_NOT_ALLOWED: "PAYMENT_METHOD_NOT_ALLOWED",
    TOTAL_MISMATCH: "TOTAL_MISMATCH",
    RATE_LIMITED: "RATE_LIMITED",
    KITCHEN_UNREACHABLE: "KITCHEN_UNREACHABLE",
    /**
     * ADR-006: fara casa fiscala rezolvabila, predarea intoarce 409 si comanda
     * NU pleaca. Nu exista varianta "continua si marcheaza pentru reconciliere".
     */
    FISCAL_PRINTER_UNAVAILABLE: "FISCAL_PRINTER_UNAVAILABLE",
    CAPABILITY_NOT_SUPPORTED: "CAPABILITY_NOT_SUPPORTED",
};
/** Coduri intoarse de core catre web si catre operatorii platformei. */
export const CORE_ERROR_CODES = {
    VALIDATION_FAILED: "VALIDATION_FAILED",
    ROUTE_NOT_FOUND: "ROUTE_NOT_FOUND",
    MARKET_NOT_FOUND: "MARKET_NOT_FOUND",
    STOREFRONT_NOT_FOUND: "STOREFRONT_NOT_FOUND",
    OFFER_NOT_AVAILABLE: "OFFER_NOT_AVAILABLE",
    ADDRESS_OUT_OF_COVERAGE: "ADDRESS_OUT_OF_COVERAGE",
    NO_VENDOR_AVAILABLE: "NO_VENDOR_AVAILABLE",
    /** ADR-003: clientul a confirmat alt vanzator decat cel afisat. */
    SELLER_MISMATCH: "SELLER_MISMATCH",
    QUOTE_EXPIRED: "QUOTE_EXPIRED",
    /** ADR-032: codul de voucher nu e valid, a expirat, e al altui client sau comanda e sub minimul lui. */
    VOUCHER_INVALID: "VOUCHER_INVALID",
    QUOTE_NOT_FOUND: "QUOTE_NOT_FOUND",
    MIN_ORDER_NOT_REACHED: "MIN_ORDER_NOT_REACHED",
    PAYMENT_METHOD_NOT_SUPPORTED: "PAYMENT_METHOD_NOT_SUPPORTED",
    PHONE_VERIFICATION_REQUIRED: "PHONE_VERIFICATION_REQUIRED",
    ORDER_NOT_FOUND: "ORDER_NOT_FOUND",
    ORDER_STATE_CONFLICT: "ORDER_STATE_CONFLICT",
    VENDOR_PAUSED: "VENDOR_PAUSED",
    VENDOR_NOT_LISTED: "VENDOR_NOT_LISTED",
    /** ADR-023: publicare blocata pe paritate de acoperire. */
    COVERAGE_PARITY_BLOCKED: "COVERAGE_PARITY_BLOCKED",
    /** ADR-012: sweep-ul de retragere ar depasi pragul de siguranta. */
    CATALOG_RETIREMENT_GUARD: "CATALOG_RETIREMENT_GUARD",
    CONFIG_VERSION_CONFLICT: "CONFIG_VERSION_CONFLICT",
    CONTRACT_UNSUPPORTED: "CONTRACT_UNSUPPORTED",
    UPSTREAM_POS_UNAVAILABLE: "UPSTREAM_POS_UNAVAILABLE",
    RATE_LIMITED: "RATE_LIMITED",
    UNAUTHORIZED: "UNAUTHORIZED",
    FORBIDDEN: "FORBIDDEN",
    INTERNAL_ERROR: "INTERNAL_ERROR",
};
// ---------------------------------------------------------------------------
// Tipuri comune
// ---------------------------------------------------------------------------
/** ADR-005: Etapa 1 accepta doar numerar la livrare. */
export const PAYMENT_METHODS = ["cash_on_delivery"];
// ---------------------------------------------------------------------------
// core -> POS: poarta `/api/delivery-platform/*` (ADR-013)
// ---------------------------------------------------------------------------
/** ADR-008: handshake de capabilitati, obligatoriu inainte de listare. */
export const POS_CAPABILITIES = [
    "catalog.feed",
    "backlog.snapshot",
    "order.commit",
    "order.cancel",
    /** ADR-032: ajustarea de suport a unei comenzi acceptate (linii/reducere), inainte de predare. */
    "order.adjust",
    /** POS-ul verifică snapshotul medical sub lock, atomic cu INSERT-ul comenzii. */
    "order.medical_snapshot_atomic_v1",
    /** POS-ul fiscalizează extra-urile cu alt TVA după alocarea reducerii semnate de Core. */
    "order.modifier_vat_allocation_v1",
    "order.handover",
    "order.delivered",
    "settlement.report",
    "fiscal.receipt",
    /**
     * Rezervari de masa prin poarta platformei. NU e in
     * `REQUIRED_POS_CAPABILITIES`: un restaurant care nu o are ramane perfect
     * vandabil, doar ca nu apare in blocul de rezervari. Fara capabilitate
     * declarata, core-ul nici nu incearca apelul — un POS mai vechi ar fi raspuns
     * 404 pe ruta, iar 404-ul s-ar fi citit ca „restaurantul nu are mese libere".
     */
    "reservation.book",
    /**
     * Poarta poate spune CE meniuri ale restaurantului alimenteaza platforma, si
     * poate schimba alegerea. Fara ea, platforma nu are cum sa afle din ce meniu
     * vine catalogul si trebuie sa trimita operatorul in interfata POS-ului.
     */
    "catalog.menus",
    /**
     * Poarta expune zonele de livrare ale unitatii (`GET /zones`), in aceeasi
     * forma pe care o trimite la provizionare. Cu ea, core-ul RESINCRONIZEAZA
     * zonele restaurantului la fiecare pull complet de catalog: POS-ul ramane
     * sursa de adevar, iar o zona schimbata acolo nu mai cere o re-legare din Hub.
     */
    "zones.feed",
    /**
     * Unitatea are livratori proprii activi in POS. Core-ul o citeste ca semnal
     * ca predarea si livrarea vin din POS (prin Hub), nu de la un curier al
     * platformei — deci dispeceratul platformei NU incearca sa aloce un curier
     * pentru comenzile ei. Se anunta dinamic: dispare cand unitatea nu mai are
     * niciun livrator activ.
     */
    "fulfillment.vendor_fleet",
];
/**
 * Cine duce comanda la client, per restaurant.
 *
 *  - `auto` (implicit): decide capabilitatea `fulfillment.vendor_fleet` din
 *    ultimul handshake — restaurantul cu livratori proprii in POS livreaza
 *    singur, celelalte primesc curierul platformei;
 *  - `vendor_fleet`: intotdeauna soferii restaurantului, oricare ar fi handshake-ul;
 *  - `platform_couriers`: intotdeauna pool-ul de curieri al pietei.
 */
export const VENDOR_FULFILLMENT_MODES = ["auto", "platform_couriers", "vendor_fleet"];
/**
 * Evenimentele pe care POS-ul le raporteaza platformei (prin Hub) despre o
 * comanda pe care o livreaza cu flota LUI. Sunt oglinda tranzitiilor din
 * `channel_orders` — nu o a doua masina de stari.
 */
export const VENDOR_FULFILLMENT_EVENTS = ["accepted", "ready", "picked_up", "delivered", "cancelled", "expired"];
/** ADR-012: peste acest prag, sweep-ul nu se aplica si se cere un `full` de confirmare. */
export const CATALOG_RETIREMENT_MAX_RATIO = 0.2;
/** ADR-009: singurul apel care atinge POS-ul pentru estimare. Cacheabil. */
export const BACKLOG_SNAPSHOT_TTL_SECONDS = 25;
// ---------------------------------------------------------------------------
// POS -> core: webhook-uri
// ---------------------------------------------------------------------------
export const POS_WEBHOOK_EVENT_TYPES = [
    "order.accepted",
    "order.preparing",
    "order.eta_changed",
    "order.ready",
    "order.handover",
    "order.rejected",
    "order.cancelled",
    "menu.availability_changed",
    "channel.paused",
    "channel.resumed",
];
//# sourceMappingURL=contracts.js.map